import { ApiRoutes } from "@/lib/routeResolver";
import { cache } from "react";
import api from "./api";

// ─── Data Fetching ───────────────────────────────────────────────────

export const fetchWebsiteRegion = cache(async (regionId: string) => {
  try {
    const res = await api.get(ApiRoutes.WEBSITE_WEBSITEREGION, {
      params: { RegionId: regionId },
    });
    return res.data?.data?.[0] || null;
  } catch {
    return null;
  }
});

export const fetchCruises = cache(
  async (page = 1, pageSize = 10, cruiseTypeName?: string) => {
    const res = await api.get(ApiRoutes.INVENTORY_PACKAGE, {
      params: {
        PageSize: pageSize,
        PageNumber: page,
        IsWebsite: true,
        OnlyWebsiteShownCruises: true,
        // Permanent cruise filter (no-op without a destination scope, but kept
        // uniform with the scoped/region cruise listings).
        IsMainDestination: true,
        BrandId: process.env.NEXT_PUBLIC_BRAND_ID_CENTRAL_HOLIDAYS,
        ...(cruiseTypeName ? { CruiseTypeName: cruiseTypeName } : {}),
      },
    });

    return {
      cruises: res.data?.data ?? [],
      totalPages: res.data?.totalPages ?? 0,
      count: res.data?.count ?? 0,
    };
  },
);

// ─── Cruise region/destination listing ──────────────────────────────

const BRAND_ID = process.env.NEXT_PUBLIC_BRAND_ID_CENTRAL_HOLIDAYS;
const CRUISE_PAGE_SIZE = 10;

/** Header-menu shape: regions, each with its destinations (countries). */
export interface CruiseRegionMenu {
  regionId: string;
  regionName: string;
  pageUrl: string;
  countries: {
    countryId: string;
    countryName: string;
    pageUrl: string;
  }[];
}

/**
 * Scope of a cruise listing page. The same listing body serves both a region
 * page and a destination page; the scope captures the only differences — which
 * id is sent, and (critically) which query-param name the `used_*` endpoints
 * expect. Note the backend's MISSPELLED `destionationId` for destinations: the
 * correctly spelled `destinationId` is silently ignored and returns unfiltered
 * global data, so we must use the typo verbatim.
 */
export type CruiseScope =
  | { kind: "region"; id: string }
  | { kind: "destination"; id: string };

/** A cruise package row as returned by `inventory/package`. */
export interface CruisePackageItem {
  id: string;
  packageName: string;
  pageURL: string;
  days: number;
  nights?: number;
  cruiseNights?: number;
  websiteOrder: number;
  photoGallery: { url?: string; title?: string }[] | null;
  packageRegions: {
    regionId?: string;
    countryId?: string;
    countryName: string;
    cityDays: { cityName: string }[];
  }[];
  rate: { startingFrom?: string | number } | null;
  packageTypeName?: string;
  isNew?: boolean;
}

export interface CruiseTypeOption {
  id: string;
  name: string;
  description?: string | null;
}

export interface CruiseShipOption {
  id: string;
  cruiseTypeId?: string;
  shipName: string;
}

/** Map a scope onto the `inventory/package` id param (RegionId | CountryId). */
function packageScopeParams(scope: CruiseScope): Record<string, string> {
  return scope.kind === "region"
    ? { RegionId: scope.id }
    : { CountryId: scope.id };
}

/** Map a scope onto the `used_*` id param (regionId | destionationId[sic]). */
function usedScopeParams(scope: CruiseScope): Record<string, string> {
  return scope.kind === "region"
    ? { regionId: scope.id }
    : { destionationId: scope.id };
}

/** Fetch the region → destination tree that drives the header Cruises menu. */
export const fetchCruiseRegionsMenu = cache(
  async (): Promise<CruiseRegionMenu[]> => {
    try {
      const res = await api.get(
        ApiRoutes.INVENTORY_PACKAGE_CRUISE_REGIONS_DESTINATIONS,
      );
      return (res.data ?? []) as CruiseRegionMenu[];
    } catch {
      return [];
    }
  },
);

/**
 * Resolve a region slug → its websiteregion record. Matches the slug against
 * `pageSEO.pageURL`, falling back to the first item. Returns null when absent
 * (caller should `notFound()`).
 */
export const resolveCruiseRegion = cache(async (slug: string) => {
  try {
    const res = await api.get(ApiRoutes.WEBSITE_WEBSITEREGION, {
      params: { pageUrl: slug },
    });
    const items = (res.data?.data ?? []) as Record<string, unknown>[];
    if (items.length === 0) return null;
    return (
      items.find((r) => (r.pageSEO as { pageURL?: string })?.pageURL === slug) ??
      items[0]
    );
  } catch {
    return null;
  }
});

/**
 * Resolve a destination slug → its websitedestination record. Matches the slug
 * against `pageSEO.pageURL`, falling back to the first item. Returns null when
 * absent (caller should `notFound()`).
 */
export const resolveCruiseDestination = cache(async (slug: string) => {
  try {
    const res = await api.get(ApiRoutes.WEBSITE_WEBSITEDESTINATION, {
      params: { pageUrl: slug },
    });
    const items = (res.data?.data ?? []) as Record<string, unknown>[];
    if (items.length === 0) return null;
    return (
      items.find((r) => (r.pageSEO as { pageURL?: string })?.pageURL === slug) ??
      items[0]
    );
  } catch {
    return null;
  }
});

/** Cruise packages for a scope, optionally narrowed by cruise type / ship. */
export async function fetchCruisePackagesByScope(
  scope: CruiseScope,
  {
    page = 1,
    cruiseTypeName,
    cruiseShipId,
  }: { page?: number; cruiseTypeName?: string; cruiseShipId?: string } = {},
) {
  try {
    const res = await api.get(ApiRoutes.INVENTORY_PACKAGE, {
      params: {
        PageSize: CRUISE_PAGE_SIZE,
        PageNumber: page,
        IsWebsite: true,
        OnlyWebsiteShownCruises: true,
        // Match the scope's destination against the package's MAIN destination
        // only — not itinerary stop points. Must mirror the client-side
        // `fetchPackages` in CruiseListingClient so SSR page 1 and paginated
        // pages stay consistent.
        IsMainDestination: true,
        BrandId: BRAND_ID,
        ...packageScopeParams(scope),
        ...(cruiseTypeName ? { CruiseTypeName: cruiseTypeName } : {}),
        ...(cruiseShipId ? { CruiseShipId: cruiseShipId } : {}),
      },
    });
    return {
      packages: res.data?.data ?? [],
      totalPages: res.data?.totalPages ?? 0,
      count: res.data?.count ?? 0,
    };
  } catch {
    return { packages: [], totalPages: 0, count: 0 };
  }
}

/** Cruise types in use within a scope (drives the "Cruise Type" filter). */
export async function fetchUsedCruiseTypes(
  scope: CruiseScope,
): Promise<CruiseTypeOption[]> {
  try {
    const res = await api.get(ApiRoutes.INVENTORY_PACKAGE_USED_CRUISE_TYPES, {
      params: usedScopeParams(scope),
    });
    return (res.data ?? []) as CruiseTypeOption[];
  } catch {
    return [];
  }
}

/** Cruise ships in use within a scope, optionally narrowed by cruise type. */
export async function fetchUsedCruiseShips(
  scope: CruiseScope,
  cruiseTypeId?: string,
): Promise<CruiseShipOption[]> {
  try {
    const res = await api.get(ApiRoutes.INVENTORY_PACKAGE_USED_CRUISE_SHIPS, {
      params: {
        ...usedScopeParams(scope),
        ...(cruiseTypeId ? { cruiseTypeId } : {}),
      },
    });
    return (res.data ?? []) as CruiseShipOption[];
  } catch {
    return [];
  }
}

/** Ship detail (photo + pageUrl) for the "Cruise Ships" image cards. */
export async function fetchCruiseShipDetail(shipId: string) {
  try {
    const res = await api.get(ApiRoutes.INVENTORY_CRUISE_BY_CRUISE_SHIP_ID, {
      params: { CruiseShipId: shipId },
    });
    return res.data?.data ?? null;
  } catch {
    return null;
  }
}

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export async function fetchCruisesClient(
  page = 1,
  pageSize = 10,
  cruiseTypeName?: string,
) {
  const params = new URLSearchParams({
    PageNumber: String(page),
    PageSize: String(pageSize),
    IsWebsite: "true",
    OnlyWebsiteShownCruises: "true",
    // Mirror the SSR `fetchCruises` filter so client pagination stays in sync.
    IsMainDestination: "true",
    CompanyId: COMPANY_ID,
  });
  if (BRAND_ID) params.set("BrandId", BRAND_ID);
  if (cruiseTypeName) params.set("CruiseTypeName", cruiseTypeName);

  const res = await fetch(`${BASE_URL}/inventory/package?${params}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN}`,
    },
  });
  const json = await res.json();
  return {
    cruises: json.data ?? [],
    totalPages: json.totalPages ?? 0,
    count: json.count ?? 0,
  };
}
